import { AxiosError } from 'axios';

/**
 * Extract a user-facing error message from an unknown catch value.
 * Handles Axios errors (response.data.detail or response.data.message),
 * native Error instances, and arbitrary values.
 */
export function getErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as Record<string, unknown> | undefined;
    if (typeof data?.detail === 'string') return data.detail;
    // FastAPI renvoie les erreurs de validation (422) dans un `detail` qui est
    // une LISTE d'objets `{loc, msg, type}`, pas une chaîne. Sans cette branche,
    // toutes les règles métier validées par Pydantic — politique de mot de
    // passe, champs obligatoires, formats — s'affichaient
    // « Request failed with status code 422 » : le message de repli d'axios,
    // qui ne dit rien à personne. Constaté sur la création d'un mot de passe
    // d'agence, mais le défaut touchait toute l'application.
    if (Array.isArray(data?.detail)) {
      const messages = (data!.detail as { msg?: unknown }[])
        .map((e) => (typeof e?.msg === 'string' ? e.msg : null))
        .filter((m): m is string => m !== null)
        // Pydantic préfixe ses messages de « Value error, » — vrai pour lui,
        // inutile pour l'utilisateur.
        .map((m) => m.replace(/^Value error,\s*/, ''));
      if (messages.length > 0) return messages.join(' ');
    }
    if (typeof data?.message === 'string') return data.message;
    return err.message || fallback;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

/**
 * Check if the error is an Axios error with a specific HTTP status code.
 */
export function isHttpError(err: unknown, status: number): boolean {
  return err instanceof AxiosError && err.response?.status === status;
}
